import { flatMap, uniqBy } from 'lodash';
import { Agent, AgentCIAppWorkflows, AgentCIKind, AgentCIWorkflow, AgentType } from '@/types';

export const CLAUDE_ROUTINE_FIRE_URL_PATTERN =
    /^https:\/\/api\.anthropic\.com\/v1\/claude_code\/routines\/trig_[A-Za-z0-9]{1,64}\/fire$/;

const DEFAULT_SAMPLE_AGENT_NAME = 'Claude';
const SAMPLE_WORKFLOW_NAME_PATTERN = /^Runway triage agent \((.*)\)$/;

export const sampleWorkflowName = (agentName: string) =>
    `Runway triage agent (${agentName.trim() || DEFAULT_SAMPLE_AGENT_NAME})`;

const slugify = (text: string) =>
    text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

export const sampleWorkflowFile = (workflowName: string) => {
    const agentName = SAMPLE_WORKFLOW_NAME_PATTERN.exec(workflowName)?.[1] ?? workflowName;
    return `.github/workflows/runway-triage-${slugify(agentName) || slugify(DEFAULT_SAMPLE_AGENT_NAME)}.yml`;
};

// The name is stored with the agent's workflows, so renaming the agent keeps matching the committed file
export const agentSampleWorkflowName = (agent: Agent) =>
    Object.values(agent.ciWorkflows ?? {})[0]?.workflow.workflowName ?? sampleWorkflowName(agent.name);

export const isSampleCIKind = (kind?: AgentCIKind) => !!kind && kind !== AgentCIKind.OwnWorkflow;

export const matchSampleWorkflow = (
    options: AgentCIAppWorkflows | undefined,
    workflowName: string,
): AgentCIWorkflow | undefined => options?.workflows.find(({ workflow }) => workflow.workflowName === workflowName);

export const suggestedAgentNameBase = (type: AgentType, ciKind: AgentCIKind) => {
    switch (type) {
        case AgentType.CIWorkflow:
            return (
                ciKind === AgentCIKind.CodexApiKey ? 'Codex'
                : ciKind === AgentCIKind.OwnWorkflow ? 'Triage agent'
                : 'Claude'
            );
        case AgentType.ClaudeRoutine:
            return 'Claude routine';
        default:
            return 'Triage agent';
    }
};

export const suggestAgentName = (base: string, takenLowercaseNames: string[]) => {
    let candidate = base;
    for (let n = 2; takenLowercaseNames.includes(candidate.toLowerCase()); n++) {
        candidate = `${base} ${n}`;
    }
    return candidate;
};

export interface RepoWorkflowLink {
    repo: string;
    url: string;
    found: boolean;
}

export const gitHubNewFileUrl = (repo: string, branch: string, file: string) =>
    `https://github.com/${repo}/new/${branch}?filename=${encodeURIComponent(file)}`;

// A workflow's GitHub URL is /blob/<branch>/<path>; /edit/ opens the same file in the editor
const gitHubEditFileUrl = (fileUrl: string) => fileUrl.replace('/blob/', '/edit/');

export const repoWorkflowLinks = (
    appIds: string[],
    options: Record<string, AgentCIAppWorkflows> | undefined,
    workflowName: string,
): RepoWorkflowLink[] =>
    uniqBy(
        flatMap(appIds, (appId): RepoWorkflowLink[] => {
            const appOptions = options?.[appId];
            if (!appOptions?.integrationIds.length) {
                return [];
            }
            const { repo, defaultBranch } = appOptions;
            const match = matchSampleWorkflow(appOptions, workflowName);
            if (match) {
                return match.workflow.workflowUrl ?
                        [{ repo, url: gitHubEditFileUrl(match.workflow.workflowUrl), found: true }]
                    :   [];
            }
            return defaultBranch ?
                    [
                        {
                            repo,
                            url: gitHubNewFileUrl(repo, defaultBranch, sampleWorkflowFile(workflowName)),
                            found: false,
                        },
                    ]
                :   [];
        }),
        ({ repo }) => repo,
    );

export const gitHubSecretsUrl = (repo: string) => `https://github.com/${repo}/settings/secrets/actions/new`;

// A plain YAML scalar can't hold every agent name, e.g. one with ": " in it
export const withWorkflowName = (sample: string, workflowName: string) => {
    const yamlName = /^[\w ().-]+$/.test(workflowName) ? workflowName : JSON.stringify(workflowName);
    return sample.replace(/^name: .*/, `name: ${yamlName}`);
};
